import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { PlayFilledIcon, SplitText, useReveal } from 'components';
import { FILM_EMBED_URL } from 'src/constants/misc';
import { gsap, useGSAP } from 'src/utils/gsap';
import { prefersReducedMotion } from 'src/utils/motion';

import { usePage } from '../../pages';

import style from './Film.module.scss';

const Film = () => {
  const { t } = useTranslation('page');
  const { id } = usePage();
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const posterRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useReveal(sectionRef);

  // Frame widens to full width and the poster drifts while scrolling
  useGSAP(
    () => {
      if (prefersReducedMotion) return;

      gsap.fromTo(
        frameRef.current,
        { clipPath: 'inset(0% 9% 0% 9% round 40px)' },
        {
          clipPath: 'inset(0% 0% 0% 0% round 20px)',
          ease: 'none',
          scrollTrigger: { trigger: frameRef.current, start: 'top 95%', end: 'top 22%', scrub: true }
        }
      );
      gsap.fromTo(
        posterRef.current,
        { yPercent: -6 },
        {
          yPercent: 6,
          ease: 'none',
          scrollTrigger: { trigger: frameRef.current, start: 'top bottom', end: 'bottom top', scrub: true }
        }
      );
    },
    { scope: sectionRef }
  );

  return (
    <section
      id='film'
      ref={sectionRef}
      className='sec tone-light'
      data-tone='light'
    >
      <div className={cn('wrap', style.head)}>
        <SplitText
          as='h2'
          className='h2'
          reveal
        >
          {t(`${id}.film.title`)}
        </SplitText>
        <p
          className='lead'
          data-reveal
        >
          {t(`${id}.film.lead`)}
        </p>
      </div>
      <div className={style.filmWrap}>
        <div
          ref={frameRef}
          className={style.frame}
        >
          <div
            ref={posterRef}
            className={style.poster}
          >
            <div
              className={style.v}
              aria-hidden='true'
            >
              <i />
              <i />
            </div>
          </div>
          {playing ? (
            // The player loads only after the click, so it starts right away with sound
            <iframe
              className={style.video}
              src={`${FILM_EMBED_URL}?autoplay=true&loop=false&muted=false&preload=true&responsive=false`}
              title={`${t(`${id}.film.videoTitle`)} ${t(`${id}.film.videoTitleAccent`)}`}
              allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen'
              allowFullScreen
            />
          ) : (
            <>
              <button
                className={style.play}
                type='button'
                aria-label={t(`${id}.film.play`)}
                onClick={() => setPlaying(true)}
              >
                <PlayFilledIcon />
              </button>
              <div className={style.meta}>
                <p className={style.videoTitle}>
                  {t(`${id}.film.videoTitle`)} <span className='serif-i'>{t(`${id}.film.videoTitleAccent`)}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Film;
